// Main extraction scanner for root-level system variable declarations.
// It returns clean source plus typed variable registries and warnings.
import type { LuisCompileWarning } from '../../types'
import { countToken } from '../../utils'
import { assignDeclarations } from './assign'
import { extractLeadingVariableDeclarations } from './parser'
import {
  isRootInlineStart,
  isStructuralRemainder,
  splitRootInlineLine,
  startsRootBlockLine,
  startsVariableDeclaration,
} from './root'
import type { ExtractedSystemVariables, ParsedVariableDeclaration, PendingVariableDeclaration } from './types'
import type { LuisResourceLimits, LuisResourcePolicy } from '../../resource-limits'

// Extract top-level `--name: value` entries and return remaining source.
export function extractSystemVariables(
  source: string,
  collectWarnings = true,
  options?: {
    sourceId?: string
    resourcePolicy?: LuisResourcePolicy
    resourceLimits?: LuisResourceLimits
  },
): ExtractedSystemVariables {
  // Return early when source cannot contain top-level system variable declarations.
  if (!source.includes('--') && !source.includes(':root')) {
    return {
      cleanSource: source,
      variables: {},
      classVariables: {},
      objectVariables: {},
      arrayVariables: {},
      warnings: [],
    }
  } else {
    // Continue full variable extraction scan.
  }

  const variables: Record<string, string> = {}
  const classVariables: Record<string, string> = {}
  const objectVariables = {}
  const arrayVariables = {}
  const warnings: LuisCompileWarning[] = []
  const keptLines: string[] = []
  const lines = source.split(/\r?\n/)

  let depth = 0
  let insideRootBlock = false
  let rootBraceBalance = 0
  let pending: PendingVariableDeclaration | null = null
  const sourceId = options?.sourceId ?? '<inline>'
  const resourcePolicy = options?.resourcePolicy ?? 'fail-closed'
  const resourceLimits = options?.resourceLimits

  // Scan source line-by-line to keep extraction and structural depth aligned.
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const line = lines[lineIndex]
    const trimmed = line.trim()
    const currentLine = lineIndex + 1

    // Continue collecting multi-line declarations until semicolon appears.
    if (pending) {
      pending.source = `${pending.source}\n${trimmed}`
      const pendingResult = extractLeadingVariableDeclarations(pending.source)
      const pendingRemainder = pendingResult.remainder.trimStart()

      if (pendingResult.declarations.length > 0 && isStructuralRemainder(pendingRemainder)) {
        assignDeclarations(
          withDeclarationLine(pendingResult.declarations, pending.line),
          variables,
          classVariables,
          objectVariables,
          arrayVariables,
          warnings,
          collectWarnings,
          sourceId,
          resourcePolicy,
          resourceLimits,
        )

        // Preserve structural remainder such as trailing `}`.
        if (pendingRemainder.length > 0) {
          keptLines.push(pendingRemainder)
          updateStructureState(pendingRemainder)
        } else {
          // Continue when pending remainder is empty.
        }

        pending = null
      } else {
        // Keep pending state until declaration boundary becomes complete.
      }

      continue
    } else {
      // Continue top-level extraction when no pending declaration exists.
    }

    const startsRootInline = isRootInlineStart(trimmed, depth, insideRootBlock)
    if (startsRootInline) {
      const rootSplit = splitRootInlineLine(line)
      if (rootSplit.prefix.trim().length > 0) {
        keptLines.push(rootSplit.prefix)
        updateStructureState(rootSplit.prefix)
      } else {
        // Continue when root-inline prefix is empty.
      }

      const inlineContent = rootSplit.inline.trimStart()
      if (!inlineContent) {
        continue
      } else {
        // Continue inline-root declaration extraction.
      }

      const inlineResult = extractLeadingVariableDeclarations(inlineContent)
      const inlineRemainder = inlineResult.remainder.trimStart()
      if (inlineResult.declarations.length > 0) {
        assignDeclarations(
          withDeclarationLine(inlineResult.declarations, currentLine),
          variables,
          classVariables,
          objectVariables,
          arrayVariables,
          warnings,
          collectWarnings,
          sourceId,
          resourcePolicy,
          resourceLimits,
        )
      } else {
        // Continue when inline content does not yield declarations.
      }

      if (startsVariableDeclaration(inlineRemainder)) {
        pending = { source: inlineRemainder, line: currentLine }
        continue
      } else if (inlineRemainder.length > 0) {
        // Keep non-variable trailing content such as closing braces.
        keptLines.push(inlineRemainder)
        updateStructureState(inlineRemainder)
      } else {
        // Continue when inline remainder is empty.
      }

      continue
    } else {
      // Continue normal root-context extraction.
    }

    const isRootContext = depth === 0 || insideRootBlock
    if (isRootContext && startsVariableDeclaration(trimmed)) {
      const extracted = extractLeadingVariableDeclarations(trimmed)
      const remainder = extracted.remainder.trimStart()

      if (extracted.declarations.length > 0) {
        assignDeclarations(
          withDeclarationLine(extracted.declarations, currentLine),
          variables,
          classVariables,
          objectVariables,
          arrayVariables,
          warnings,
          collectWarnings,
          sourceId,
          resourcePolicy,
          resourceLimits,
        )
      } else {
        // Continue when line has no completed declaration yet.
      }

      if (startsVariableDeclaration(remainder)) {
        pending = { source: remainder, line: currentLine }
        continue
      } else if (remainder.length > 0) {
        // Preserve structural remainder such as `}` after `--x: ...;}`.
        keptLines.push(remainder)
        updateStructureState(remainder)
      } else {
        // Continue when declaration remainder is empty.
      }

      continue
    } else {
      // Keep line untouched when it is not a root-level declaration line.
    }

    keptLines.push(line)
    updateStructureState(line)
  }

  // Keep unresolved pending text so parser reports a meaningful syntax error.
  if (pending) {
    keptLines.push(pending.source)
  } else {
    // Continue return flow when no unresolved declaration remains.
  }

  // Return extracted variable registries and clean source text.
  return {
    cleanSource: keptLines.join('\n'),
    variables,
    classVariables,
    objectVariables,
    arrayVariables,
    warnings,
  }

  // Update structural depth using only preserved source text.
  function updateStructureState(text: string): void {
    const openCount = countToken(text, '{')
    const closeCount = countToken(text, '}')
    const trimmedText = text.trim()
    const startsRootBlock = startsRootBlockLine(trimmedText, depth, insideRootBlock, openCount)

    // Enter root-block context when preserved text opens top-level `:root`.
    if (startsRootBlock) {
      insideRootBlock = true
      rootBraceBalance = openCount - closeCount
    } else if (insideRootBlock) {
      // Continue root balance tracking while still inside root context.
      rootBraceBalance += openCount - closeCount
    } else {
      // Keep root tracking unchanged outside root context.
    }

    // Exit root-block context after matching closing brace is reached.
    if (insideRootBlock && rootBraceBalance <= 0) {
      insideRootBlock = false
      rootBraceBalance = 0
    } else {
      // Keep root context active while balance is still positive.
    }

    depth += openCount - closeCount
    depth = Math.max(depth, 0)
  }
}

// Attach source line metadata to parsed variable declarations.
function withDeclarationLine(
  declarations: ParsedVariableDeclaration[],
  line: number,
): ParsedVariableDeclaration[] {
  // Return declarations with fallback line value for warnings.
  return declarations.map((declaration) => ({
    ...declaration,
    line: declaration.line ?? line,
  }))
}
